import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Eye, EyeOff, Printer, HelpCircle, Home, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AppShell from '@/components/AppShell';
import PrintHeader from '@/components/PrintHeader';
import PrintReadyMarker from '@/components/PrintReadyMarker';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';
import SaveToLibraryButton from '@/components/library/SaveToLibraryButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkeletonNotes() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTeacher, setShowTeacher] = useState(false);

  useEffect(() => {
    fetchData();
  }, [unitId]);

  const fetchData = async () => {
    try {
      const [unitRes, notesRes] = await Promise.all([
        axios.get(`${API}/units/${unitId}`),
        axios.get(`${API}/skeleton-notes/${unitId}`)
      ]);
      setUnit(unitRes.data);
      setNotes(notesRes.data);
      if (notesRes.data.length > 0) setSelectedNote(notesRes.data[0]);
    } catch {
      toast.error('Failed to load skeleton notes');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();
  const [coverOpen, setCoverOpen] = useState(false);
  const handleDownloadPdf = () => {
    if (!unitId) return;
    setCoverOpen(true);
  };
  const handleCoverConfirm = async (cover) => {
    const filename = `skeleton-notes-${(unit?.title || 'unit').replace(/[^a-z0-9-]+/gi, '-').toLowerCase().slice(0, 40)}.pdf`;
    await downloadPdf({
      resource: 'skeleton-notes',
      resourceId: unitId,
      filename,
      cover: {
        ...cover,
        title: unit?.title,
        grade: unit?.grade_level ? String(unit.grade_level) : '',
        unit_number: unit?.unit_number != null ? String(unit.unit_number) : '',
        resource_label: 'Skeleton Notes',
        subtitle: selectedNote?.title,
      },
    });
    setCoverOpen(false);
  };

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const key = `${i}-${line.slice(0, 30)}`;
      if (line.startsWith('# ')) return <h2 key={key} className="text-xl font-serif text-[#14213D] mt-6 mb-3">{line.slice(2)}</h2>;
      if (line.startsWith('## ')) return <h3 key={key} className="text-lg font-serif text-[#14213D] mt-5 mb-2">{line.slice(3)}</h3>;
      if (line.startsWith('### ')) return <h4 key={key} className="text-base font-semibold text-[#14213D] mt-4 mb-2">{line.slice(4)}</h4>;
      if (line.includes('_____')) {
        const parts = line.split('_____');
        return (
          <p key={key} className="text-[#2D3748] leading-relaxed mb-1.5">
            {parts.map((part, j) => (
              <React.Fragment key={`${key}-part-${j}`}>
                {part}{j < parts.length - 1 && <span className="inline-block w-28 border-b-2 border-[#CBD5E0] mx-1 align-bottom" />}
              </React.Fragment>
            ))}
          </p>
        );
      }
      if (line.match(/^\d+\./)) return <p key={key} className="text-[#2D3748] leading-relaxed mb-1.5 pl-4">{line}</p>;
      if (line.startsWith('- ') || line.startsWith('• ')) return <p key={key} className="text-[#2D3748] leading-relaxed mb-1 pl-6">&bull; {line.slice(2)}</p>;
      if (line.trim() === '') return <div key={key} className="h-3" />;
      return <p key={key} className="text-[#2D3748] leading-relaxed mb-1.5">{line}</p>;
    });
  };

  if (loading) return <AppShell><div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:max-w-none print:px-0 print:py-2">
        <PrintReadyMarker ready={!loading && !!unit && notes.length >= 0} />
        <PrintHeader
          resourceType="Skeleton Notes"
          resourceTitle={selectedNote?.title}
          grade={unit?.grade_level}
          unitNumber={unit?.unit_number}
          unitTitle={unit?.title}
        />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 print:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/unit/${unitId}`)}
            className="text-[#5A6577] hover:text-[#14213D]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Unit
          </Button>
          <div className="flex items-center gap-2">
            {selectedNote && (
              <SaveToLibraryButton
                artifactType="skeleton_notes"
                title={selectedNote.title || `${unit?.title || 'Skeleton Notes'}`}
                description={`Skeleton notes for ${unit?.title || 'this unit'}`}
                gradeLevel={unit?.grade_level}
                unitId={unitId}
                unitTitle={unit?.title}
                content={selectedNote}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => setShowTeacher(!showTeacher)}>
              {showTeacher ? <><EyeOff className="h-4 w-4 mr-2" />Hide Answers</> : <><Eye className="h-4 w-4 mr-2" />Show Answers</>}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-btn"
              title="Download this resource as a PDF"
            >
              {downloadingPdf ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating PDF&hellip;</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />Download PDF</>
              )}
            </Button>
          </div>
        </div>

        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Skeleton Notes</p>
          <h2 className="font-serif-display text-3xl text-[#14213D] tracking-tight mb-2">{unit?.title}</h2>
          <p className="text-sm text-[#5A6577]">Grade {unit?.grade_level} &middot; Unit {unit?.unit_number}</p>
        </div>

        {notes.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 text-[#CBD5E0] mx-auto mb-4" />
            <h3 className="text-xl font-serif text-[#14213D] mb-2">No Skeleton Notes Available</h3>
            <p className="text-[#5A6577]">Skeleton notes for this unit are being prepared.</p>
          </Card>
        ) : selectedNote ? (
          <div>
            <Tabs defaultValue="student">
              <TabsList className="mb-6 print:hidden">
                <TabsTrigger value="student">Student Version</TabsTrigger>
                <TabsTrigger value="teacher">Teacher Guide</TabsTrigger>
                <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
              </TabsList>

              <TabsContent value="student">
                <Card className="p-8 print:p-0 print:shadow-none print:border-0" data-print-block>
                  <div className="prose-stone max-w-none">
                    {renderContent(selectedNote.student_content)}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="teacher">
                <Card className="p-8 bg-[#FBF6E8]/50 print:p-0 print:bg-white print:shadow-none print:border-0" data-print-block>
                  <div className="mb-4 p-3 bg-[#FBF6E8] border border-yellow-300 rounded-lg print:hidden">
                    <p className="text-sm text-[#14213D] font-medium">Teacher Guide — All blanks filled in with answers and teaching tips</p>
                  </div>
                  <div className="prose-stone max-w-none">
                    {renderContent(selectedNote.teacher_guide)}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="side-by-side">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-1 print:gap-2">
                  <Card className="p-6 print:p-0 print:shadow-none print:border-0" data-print-block>
                    <h3 className="text-sm font-semibold text-[#5A6577] uppercase tracking-wider mb-4">Student Version</h3>
                    <div className="text-sm">{renderContent(selectedNote.student_content)}</div>
                  </Card>
                  <Card className="p-6 bg-[#FBF6E8]/50 print:p-0 print:bg-white print:shadow-none print:border-0" data-print-block>
                    <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Teacher Guide</h3>
                    <div className="text-sm">{renderContent(selectedNote.teacher_guide)}</div>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>

            {selectedNote.study_questions?.length > 0 && (
              <Card className="p-6 mt-6 print:p-0 print:mt-4 print:shadow-none print:border-0 print:border-t print:pt-4" data-print-block>
                <h3 className="font-serif text-lg text-[#14213D] mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-yellow-600" /> Study Questions
                </h3>
                <div className="space-y-3">
                  {selectedNote.study_questions.map((q, i) => (
                    <div key={`sq-${i}-${q.slice(0, 30)}`} className="flex items-start gap-3 p-3 bg-[#F9F8F4] rounded-lg">
                      <span className="font-bold text-yellow-600 mt-0.5">{i + 1}.</span>
                      <p className="text-[#14213D]">{q}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        ) : null}
      </div>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={handleCoverConfirm}
        downloading={downloadingPdf}
        context={{
          title: unit?.title,
          resource_label: 'Skeleton Notes',
          grade: unit?.grade_level,
          unit_number: unit?.unit_number,
        }}
      />
    </AppShell>
  );
}
